import {
  Grid,
  Typography,
  Menu,
  MenuItem,
  Paper,
  styled,
  useTheme,
} from '@silverstein-properties/inspirelabs-ui';
import RoundButton from 'components/RoundButton/Component';
import { useState } from 'react';
import { formatTimeRange } from '@/utils';

import { RepeatIcon, Tooltip } from '@silverstein-properties/inspirelabs-ui';
import { CalendarListing, ExperienceStatus, ListingStatus } from '@/types';
import { ThumbnailGraffitiImage } from '@/assets';
import { CustomersPopover } from '../CustomersPopover';

export const StyledImage = styled('img')(({ theme }) => ({
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
}));

const mapListingStatus = (status: ListingStatus | undefined): string => {
  return status === ListingStatus.MERCHANT_CANCELLED
    ? 'Cancelled'
    : 'Published';
};

export interface ListItemProps {
  startDate?: Date | null;
  endDate?: Date | null;
  disableActions?: boolean;
  onEdit?: () => void;
  event?: CalendarListing;
}

export const ListItem = ({
  startDate,
  endDate,
  disableActions,
  onEdit,
  event,
}: ListItemProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const status = mapListingStatus(event?.status);
  const isInProgress = status === ExperienceStatus[0];
  const isPendingApproval = status === ExperienceStatus[1];
  const isApproved = status === ExperienceStatus[3];

  const nbOfCancellations = event?.nbOfCancellations;

  const timeRange = formatTimeRange(startDate, endDate);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit?.();
    handleCloseMenu();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: theme.spacers.m,
        cursor: 'auto',
      }}
    >
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <StyledImage
            src={event?.productSnapshot?.coverPhoto || ThumbnailGraffitiImage}
            alt={event?.productSnapshot?.title || 'Unknown title'}
            height={80}
            width={120}
          />
        </Grid>
        <Grid item xs>
          <Typography variant="subtitle2">
            {event?.productSnapshot?.title || 'Unknown title'}
          </Typography>
        </Grid>
        <Grid item xs display="flex" justifyContent="flex-end">
          {Number(event?.nbOfGuests || 0) > 0 ? (
            <CustomersPopover
              bookings={event?.bookings || []}
              capacity={event?.capacity}
              title={event?.productSnapshot?.title || 'Unknown title'}
              timeRange={timeRange}
              reccuringEvent={event?.reccuringEvent}
            >
              <Typography
                sx={{ textDecoration: 'underline' }}
                variant="body3"
              >{`${event?.nbOfGuests}/${event?.capacity} customers`}</Typography>
            </CustomersPopover>
          ) : (
            <Typography sx={{ opacity: 0.5 }} variant="body3">
              0 customers
            </Typography>
          )}
        </Grid>
        <Grid item xs display="flex" justifyContent="flex-end">
          <Typography variant="body3" color="error">
            {nbOfCancellations ? `${nbOfCancellations} cancelled` : null}
          </Typography>
        </Grid>

        <Grid
          item
          xs
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
        >
          <div className="timeListBlock">
            <Typography variant="body3">{timeRange}</Typography>
            {!!event?.isRecurringEvent && (
              <Tooltip arrow title="This is a recurring event." placement="top">
                <RepeatIcon
                  width={16}
                  color="primary"
                  style={{ marginLeft: theme.spacing(0.75) }}
                />
              </Tooltip>
            )}
          </div>
          <Typography
            variant="body3"
            color={
              status === 'Cancelled'
                ? theme.palette.error.main
                : theme.palette.success.main
            }
          >
            {status}
          </Typography>
        </Grid>
        <Grid
          item
          xs={1.2}
          sx={{ '>*': { float: 'right' }, position: 'relative' }}
        >
          {!disableActions ? (
            <>
              <RoundButton
                text={isInProgress ? 'Finish' : 'Actions'}
                default={`${isInProgress ? 'blue' : 'white-navy'} small`}
                hover={`${isInProgress ? 'white-navy' : 'blue'} small`}
                handleClick={handleOpenMenu}
              />
              {!isInProgress ? (
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  sx={{ mt: theme.spacers.s }}
                  disableRestoreFocus
                >
                  {!isPendingApproval ? (
                    <MenuItem disableRipple onClick={handleEdit}>
                      View / Edit
                    </MenuItem>
                  ) : null}
                  {!isApproved ? (
                    <MenuItem disableRipple onClick={handleCloseMenu}>
                      Delete
                    </MenuItem>
                  ) : null}
                </Menu>
              ) : null}
            </>
          ) : null}
        </Grid>
      </Grid>
    </Paper>
  );
};
